import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Row, Table } from "react-bootstrap";
import { InputLabel, Skeleton, TableCell, Tooltip } from "@mui/material";
import {
    AddCircleOutline,
    DescriptionOutlined,
    History
} from "@mui/icons-material";
import Select from "react-select";
import moment from "moment";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import PriorityHistoryModal from "./PriorityHistoryModal";
import AssignPractitionerModal from "./AssignPractitionerModal";
import AddNoteModal from "./AddNoteModal";
import ViewNotesModal from "./ViewNotesModal"
import TaskDetailsModal from "./TaskDetailsModal";
import CreateTaskModal from "./CreateTaskModal";
import Search from "./Search";

// shared UI
import { Button } from "../../common/Button";
import MultiSelect from "../../common/textfield/MultiSelect";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";

import { searchTasks, updateTask } from "../../api/Task";
import { getPractitionerAuthorizedList } from "../../api/Individual";

import { decryptData } from "../../EncryptDecrypt";

import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";

export const PRIORITY_LEVELS = [1, 2, 3, 4, 5];

function TaskList() {
    const myOrgId = useSelector(
         (state) => state?.auth?.user?.organizations?.[0]?.id || ""
     );
    const [orgId, setOrgId] = useState(null);

    const myPractId = useSelector(
        s => s?.auth?.user?.["custom:unique_id"]
    );
    const practitionerId = useSelector(
      (state) => state?.auth?.user?.["custom:practitioner_id"]
    );
      
    const actorDisplay = useSelector(
        (state) => state?.auth?.user?.name?.[0]?.text
    );

    const formik = useFormik({
        initialValues: { selectedPriorityLevels: [], nameFilter: ""},
    });
    const { selectedPriorityLevels, nameFilter } = formik.values;

    const [isLoading, setIsLoading] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [page, setPage] = useState(0);
    const [priorityHistoryModal, setPriorityHistoryModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [assignPractData, setAssignPractData] = useState(null);
    const [assignPractModalShow, setAssignPractModalShow] = useState(false);

    const [addNoteModalShow, setAddNoteModalShow] = useState(false);
    const [viewNoteModalShow, setViewNoteModalShow] = useState(false);
    const [noteModalData, setNoteModalData] = useState(null);

    const [selectedType, setSelectedType] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState({
        value: "new",
        label: "New",
    });
    const [selectedPractitioner, setSelectedPractitioner] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [viewTaskData, setViewTaskData] = useState(null);
    const [taskModalShow,setTaskModalShow] = useState(false);

    const [practitionersUnderOrg, setPractitionersUnderOrg] = useState([]);
    const [PatientList, setPatientList] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [orgData, setOrgdata] = useState({})
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null)
    let value = ""
    useOrgQuery({
        onSuccess: (data) => {
            setOrgdata(data);
            setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id }))) 
        },
        refetchOnWindowFocus: false,
    })

    const filterByName = () => {
        if(formik?.values?.nameFilter?.length > 0) {
            let searchVal = formik.values.nameFilter;
            const temp = (searchVal.trim().length > 0) ? tasks?.filter((data) => (data?.patient?.display?.toLowerCase()?.includes(searchVal))) : tasks;
            setFilteredTasks(temp);
        }        
    }
    const handleOrgChange = (event) => {
      const value = event?.value ?? event?.target?.value ?? event ?? "";
      setOrgId(value);
      setSelectedPractitioner(null);
      setPractitionersUnderOrg([]);
      setPage(0);
    };

    const handleFilterData = (data) => {
        setFilteredTasks(data);
        page !== 0 && setPage(0);
    };


    useEffect(() => {
        const fetch = async () => {
            try {
                setIsLoading(true);
                // Always call API with myOrgId (gets all authorized tasks)
            const res = await searchTasks({ orgId: myOrgId, status: selectedStatus.value });

                let resources =
                    res?.data?.entry?.map(({ resource }) => ({
                        ...resource,
                        id: resource.id || resource.taskId,
                        type: resource.code?.text,
                        priority: resource.priority,
                        patient: resource.for,
                        description: resource.description,
                        created: resource.authoredOn || resource.createdAt,
                        notes: resource.notes,
                        priorityHistory: resource.priorityHistory,
                        status: resource.status,
                        practitionerAssignment: { actor: resource.owner }
                    })) || [];

                // Filtering logic
                if (orgId && orgId.trim() !== "") {
                    resources = resources.filter(task => task?.orgId === orgId);
                }
                const sorted = resources.sort(
                    (a, b) =>
                        a.priority - b.priority ||
                        (a.created || 0) - (b.created || 0)
                );

                setTasks(sorted);
                // filter if there is a nameFilter value
                if(formik?.values?.nameFilter?.length > 0) {
                    let searchVal = formik.values.nameFilter;
                    const temp = (searchVal.trim().length > 0) ? sorted?.filter((data) => (data?.patient?.display?.toLowerCase()?.includes(searchVal))) : sorted;
                    setFilteredTasks(temp);
                } 
                else
                    setFilteredTasks(sorted);
                
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetch();
    }, [orgId, selectedStatus]);

    useEffect(() => {
        let data = tasks.filter(t => t.status === selectedStatus.value);

        if (selectedType) {
            data = data.filter((t) => t.type === selectedType.value);
        }

        if (selectedPriorityLevels.length) {
            data = data.filter((t) => selectedPriorityLevels.includes(t.priority));
        }

        if (selectedPractitioner) {
            data = data.filter(
                t =>
                    t?.practitionerAssignment?.actor?.reference?.split("/")[1] ===
                    selectedPractitioner.data.id
            );
        }

        if (selectedCreator) {
            const val = selectedCreator.value;
            data = data.filter(
                t =>
                    (t.requester?.reference ?? t.requesterRef) === val
            );
        }

        setFilteredTasks(data);
        /* 5. Patient ------------------------------------ */
        filterByName();
        
        // Only reset page to 0 when filters change, not when page changes
        setPage(0);
    }, [
        tasks,
        selectedStatus,
        selectedType,
        selectedPriorityLevels,
        selectedCreator,
        selectedPatient,
        selectedPractitioner,
    ]);

    /* ------------------------------------------------------------------ */
    /* fetch lookup tables (practitioners + patients) once orgId is ready */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        // if (!orgId) return;
        const activeOrgId = orgId || myOrgId;
        if (!activeOrgId) return;

        // ── practitioners ────────────────────────────────────────────────
        getPractitionerAuthorizedList(activeOrgId)
            .then(res => {
                if (res.status) setPractitionersUnderOrg(res.result);
            })
            .catch(err => console.error(err));

        // ── patients ─────────────────────────────────────────────────────
        // Removing the API call for GET patients until we move to a search based on names/IDs
        //getPatientList(activeOrgId)
        //    .then(res => {
        //        const patients = res?.result?.map((ele) => ({
        //            id: ele.id,
        //            label: decryptData(ele.name[0]?.text?.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')),
        //            value: ele.id,
        //        }))?.sort((a, b) => a.label.localeCompare(b.label))
        //        if (patients?.length > 1) {
        //            setPatientList(patients);
        //        }
        //    })
        //    .catch(err => console.error(err));
    }, [orgId, myOrgId]);


    const handleAddNoteClick = task => {
        setNoteModalData(task);
        setAddNoteModalShow(true);
    };

    const handleViewNotesClick = task => {
        setNoteModalData(task);
        setViewNoteModalShow(true);
    };

    const capitalizeWords = (str = "") =>
        String(str)
            .toLowerCase()
            .replace(/(^|\s)\S/g, m => m.toUpperCase());

    const handlePriorityHistoryView = (task) => {
        setSelectedRecord(task);
        setPriorityHistoryModal(true);
    };

    /** Update the priority & append to priorityHistory */
    const handleUpdatePriority = async (task, newPriority) => {
        try {
            setIsLoading(true);


            const newEntry = {
                actor: {
                    display: capitalizeWords(actorDisplay),
                    reference: `Practitioner/${practitionerId}`,
                    type: "Practitioner",
                },
                oldPriority: task.priority,
                newPriority,
                updatedAt: Date.now(),
            };

            await updateTask({
                id: task.id,
                priority: newPriority,
                orgId: task?.orgId,
                priorityHistory: [...(task.priorityHistory ?? []), newEntry],
            });

            setSelectedStatus({ ...selectedStatus });
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePatientView = (task) => {
        const baseUrl = window.location.origin;
        const patientId = task?.patient?.reference?.split("/")?.[1];
        const name = task?.patient?.display;

        if (patientId) {
            window.open(
                `${baseUrl}/app/patients-details?patientId=${patientId}`,//&patientName=${encodeURIComponent(name)}`,
                "_blank"
            );
        }
    };

    const handleAssignPractitionerClick = task => {
        setAssignPractData(task);
        setAssignPractModalShow(true);
    };

    const handleNoteAdded = (taskId, newNotesArray) => {
        setTasks(prev =>
            prev.map(t => (t.id === taskId ? { ...t, notes: newNotesArray } : t))
        );

        setNoteModalData(prev =>
            prev && prev.id === taskId ? { ...prev, notes: newNotesArray } : prev
        );
    };

    const typeOptions = [
        { label: "Admin Task", value: "Admin Task" },
        { label: "Review Lab Results", value: "Review Lab Results" },
        { label: "Schedule an Appointment", value: "Schedule an Appointment" },
    ];

    const statusOptions = ["new", "in-progress", "completed"].map(
        (s) => ({
            value: s,
            label: s.replace(/(^|-)\w/g, (c) => c.toUpperCase()),
        })
    );

    const priorityOptions = PRIORITY_LEVELS.map((n) => ({
        name: n,
        value: n,
        id: n,
    }));

    const practitionerOptions = practitionersUnderOrg
          .filter(p => p?.name?.[0]?.text)
          .map(p => ({
            value: p.id,
            label: capitalizeWords(p.name?.[0]?.text),
            data: p
          }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const patientOptions = PatientList
        .map(p => ({
            value: `Patient/${p.id}`,
            label: capitalizeWords(p?.label) || "(Unnamed)",
            data: p,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const creatorOptions = practitionersUnderOrg
        .map(pr => ({
            value: `Practitioner/${pr.id}`,
            label: pr.name?.[0]?.text?.replace(/(^|\s)\S/g, m => m.toUpperCase()),
            data: pr,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const columns = [
        "Type",
        "Priority",
        "Patient Name",
        "Organization",
        "Description",
        "Created",
        "Notes",
        "Practitioner",
        "Action",
    ];
    const rowsPerPage = 10;
    
    return (
        <>
            {isLoading && <FullPageSpinner loadingText="Loading Tasks..." />}
            <section className="common-listing">
                {/* Filters bar */}

                <div className="heading-wrap">
                    <Row className="w-100">
                        {/* 1. Status ---------------------------------------------------- */}
                        <Col className="filter-col" style={{ flex: '0 0 170px', maxWidth: 170 }}>
                            <InputLabel style={{ fontSize: "0.75rem" }}>Status</InputLabel>
                            <Select
                                value={selectedStatus}
                                onChange={setSelectedStatus}
                                options={statusOptions}
                                classNamePrefix="select"
                                isClearable={false}
                                className="search-select"
                                isMulti={false}
                            />
                        </Col>

                        {/* 2. Type ------------------------------------------------------ */}
                        <Col className="filter-col">
                            <InputLabel style={{ fontSize: "0.75rem" }}>Type</InputLabel>
                            <Select
                                value={selectedType}
                                onChange={setSelectedType}
                                options={typeOptions}
                                classNamePrefix="select"
                                isClearable
                                className="search-select"
                                isMulti={false}
                            />
                        </Col>

                        {/* 3. Priority -------------------------------------------------- */}
                        <Col className="filter-col" style={{ flex: '0 0 170px', maxWidth: 170 }}>
                            <MultiSelect
                                key={`priority-${selectedStatus.value}`}
                                options={priorityOptions}
                                keyField="selectedPriorityLevels"
                                label="Priority Levels"
                                nodata={"No Data Found"}
                                formik={formik}
                                required={false}
                                propsOptionData={0}
                            />
                        </Col>
                        {/* 4. Patient --------------------------------------------------- */}
                        {/* Removing the Patient filter until we implement a search based on names/IDs */}
                        {/*<Col className="filter-col">
                            <InputLabel style={{ fontSize: "0.75rem" }}>Patient</InputLabel>
                            <Select
                                value={selectedPatient}
                                onChange={setSelectedPatient}
                                options={patientOptions}
                                classNamePrefix="select"
                                isClearable
                                className="search-select"
                                isMulti={false}
                            />
                        </Col>*/}
                        <Col className="filter-col">
                            <InputLabel style={{ fontSize: "0.75rem" }}>Patient</InputLabel>
                            <Search list={tasks} filterData={handleFilterData} formik={formik} />
                        </Col>
                        {/* 5. Org if required -------------------------------------------------- */}
                        {organizations.length > 1 &&  <Col>
                        <InputLabel
                                id="mutiple-select-label"
                                style={{ fontSize: "0.75rem" }}
                            >
                                {"Organization"}
                            </InputLabel>
                            <div style={{ width: "200px" }}>
                            <Select
                                keyField={"organization"}
                                label={"Select Organization"}
                                selectLabel="Select"
                                options={organizations}
                                isLoading={organizations?.length > 1 ? false : true}
                                readOnly={organizations?.length > 1 ? false : true}
                                hideRequired={true}
                                onChange={handleOrgChange}
                                className="search-select"
                                isClearable
                                classNamePrefix="select"
                            />
                            </div>
                            </Col>}

                        {/* 6. Assigned Practitioner ------------------------------------ */}
                        <Col className="filter-col">
                            <InputLabel style={{ fontSize: "0.75rem" }}>Practitioner</InputLabel>
                            <Select
                                value={selectedPractitioner}
                                onChange={setSelectedPractitioner}
                                options={practitionerOptions}
                                classNamePrefix="select"
                                isClearable
                                className="search-select"
                                isMulti={false}
                            />
                        </Col>

                        {/* 7. Creator --------------------------------------------------- */}
                        <Col className="filter-col">
                            <InputLabel style={{ fontSize: "0.75rem" }}>Creator</InputLabel>
                            <Select
                                value={selectedCreator}
                                onChange={setSelectedCreator}
                                options={creatorOptions}
                                classNamePrefix="select"
                                isClearable
                                className="search-select"
                                isMulti={false}
                            />
                        </Col>

                        <Col xs="auto" className="right-wrap d-flex align-items-end justify-content-end ms-auto">
                            <Button
                                onClick={() => {setShowCreateModal(true)}}
                                className="custom-btn"
                                style={{ width: "auto" }}
                            >
                                Create
                            </Button>
                        </Col>
                    </Row>
                </div>


                {/* Table */}
                <div className="table-wrap">
                    <Table>
                        <thead>
                            <tr>
                                {columns.map((c) => (
                                    (c !== "Organization" || (c === "Organization" && organizations.length > 1)) ? <th key={c}>{c}</th> : null
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                [...Array(rowsPerPage)].map((_, idx) => (
                                    <tr key={idx}>
                                        {columns.map((c) => (
                                            <td key={c}>
                                                <Skeleton animation="wave" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : filteredTasks.length ? (
                                filteredTasks
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((task) => (
                                        <tr key={task.id}>
                                            <td>{task.type}</td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    {/* allow editing only when NOT completed */}
                                                    {["new", "in-progress"].includes(task.status) ? (
                                                        <Select
                                                            value={{ label: task.priority, value: task.priority }}
                                                            options={PRIORITY_LEVELS.map((p) => ({ label: p, value: p }))}
                                                            onChange={(opt) => handleUpdatePriority(task, opt.value)}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    ) : (
                                                        task.priority
                                                    )}

                                                    {/* history button */}
                                                    {task.priorityHistory?.length > 0 && (
                                                        <Tooltip title="View Priority History" placement="top">
                                                            <Link
                                                                className="view-btn"
                                                                onClick={() => handlePriorityHistoryView(task)}
                                                                style={{ marginLeft: 5 }}
                                                            >
                                                                <History style={{ color: "#005ba8" }} />
                                                            </Link>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </td>

                                            <td style={{ color: "#005ba8", textTransform: "capitalize", cursor: "pointer" }}>
                                                {task.patient ? (
                                                    <span
                                                        style={{
                                                            color: "#005ba8",
                                                            textDecoration: "underline",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() => handlePatientView(task)}
                                                    >
                                                        {task.patient.display}
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            {organizations.length > 1 &&                        
                                            <td
                                                style={{ maxWidth: 240, whiteSpace: "pre-wrap" }}
                                            ><span>
                                                {task.orgId ? (
                                                     organizations?.find(org => org.value === task.orgId)?.label || "-" 
                                                ) : (
                                                    "-"
                                                )}
                                            </span>
                                            </td>
                                            
                                            }
                                            <td
                                                style={{ maxWidth: 240, whiteSpace: "pre-wrap" }}
                                            >
                                                {task.description}
                                            </td>
                                            <td>{moment(task.created).format("MMM-DD-YYYY")}</td>
                                            <td style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                {/* show an “add” button when there are no notes  */}
                                                {task.notes?.length === 0 && (
                                                    <Tooltip title="Add a Note">
                                                        <Link
                                                            onClick={() => handleAddNoteClick(task)}
                                                            style={{
                                                                background: '#fff',
                                                                border: '1px solid #cee0ef',
                                                                borderRadius: '50%',
                                                                padding: 5
                                                            }}>
                                                            <AddCircleOutline style={{ color: '#005ba8' }} />
                                                        </Link>
                                                    </Tooltip>
                                                )}

                                                {/* otherwise show the “last note” preview + paper icon */}
                                                {task.notes?.length > 0 && (
                                                    <Tooltip
                                                        title={
                                                            <>
                                                                Note:&nbsp;{task.notes.at(-1).note}
                                                                <br /><br />
                                                                Added&nbsp;by:&nbsp;{capitalizeWords(task.notes.at(-1).actor?.display)}
                                                                <br />
                                                                Timestamp:&nbsp;{moment(task.notes.at(-1).updatedAt).format("DD-MMM-YY HH:mm")}
                                                            </>
                                                        }>
                                                        <Link
                                                            onClick={() => handleViewNotesClick(task)}
                                                            className="view-btn"
                                                            style={{
                                                                background: '#fff',
                                                                border: '1px solid #cee0ef',
                                                                borderRadius: '50%',
                                                                padding: 5
                                                            }}>
                                                            <DescriptionOutlined style={{ color: '#005ba8' }} />
                                                        </Link>
                                                    </Tooltip>
                                                )}
                                            </td>

                                            <td>
                                                {(() => {
                                                    const last =
                                                        task.practitionerHistory?.[task.practitionerHistory.length - 1] || {};
                                                    const lastDisplay = last?.actor?.display;

                                                    return lastDisplay && lastDisplay !== "-" ? (
                                                        /* practitioner is already assigned */
                                                        <div style={{ display: "flex" }}>
                                                            <span>{capitalizeWords(lastDisplay)}</span>

                                                            {/* history icon if there is >1 entry */}
                                                            {task.practitionerHistory.length > 0 && (
                                                                <Tooltip title="View Assigned Practitioner History" style={{ marginLeft: 5 }}>
                                                                    <Link className="view-btn"
                                                                        onClick={() => handleAssignPractitionerClick(task)}>
                                                                        <History style={{ color: "#005ba8" }} />
                                                                    </Link>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        /* no one assigned yet → show Assign button */
                                                        <Button
                                                            title="Assign Practitioner"
                                                            onClick={() => handleAssignPractitionerClick(task)}
                                                        >
                                                            Assign
                                                        </Button>
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                <Button onClick={()=>{
                                                        setViewTaskData(task);
                                                        setTaskModalShow(true);
                                                    }}>
                                                    View
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                            ) : (
                                <tr>
                                    <TableCell
                                        colSpan={columns.length}
                                        style={{ textAlign: "center" }}
                                    >
                                        No Tasks Found
                                    </TableCell>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination */}
                {filteredTasks.length > 0 && (
                    <CustomPagination
                        tableData={filteredTasks}
                        totalRows={filteredTasks.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handlePage={setPage}
                    />
                )}
            </section>

            {/* Modals (stubbed) */}
            {/* {showCreateModal && <CreateTaskModal onHide={() => setShowCreateModal(false)} />} */}
            {/* {viewTaskData && <ViewTaskModal data={viewTaskData} onHide={() => setViewTaskData(null)} />} */}

            {priorityHistoryModal && (
                <PriorityHistoryModal
                    data={selectedRecord}
                    onShow={priorityHistoryModal}
                    onHide={() => setPriorityHistoryModal(false)}
                />
            )}

            {assignPractModalShow && (
                <AssignPractitionerModal
                    data={assignPractData}
                    onShow={assignPractModalShow}
                    onHide={() => {
                        setAssignPractModalShow(false);
                        /* refresh Task list so the new assignee is shown */
                        setSelectedStatus({ ...selectedStatus });   // triggers useEffect fetch
                    }}
                    organizations={organizations}
                />
            )}

            {/* Notes Modals --------------------------------------------------------- */}
            {addNoteModalShow && (
                <AddNoteModal
                    data={noteModalData}
                    onShow={addNoteModalShow}
                    onHide={() => {
                        setAddNoteModalShow(false);
                        /* quick refresher so the table shows the new note */
                        setSelectedStatus({ ...selectedStatus });
                    }}
                    onNoteAdded={handleNoteAdded}
                />
            )}
            {viewNoteModalShow && (
                <ViewNotesModal
                    recordData={noteModalData}
                    onShow={viewNoteModalShow}
                    onHide={() => setViewNoteModalShow(false)}
                    onNoteAdded={handleNoteAdded}
                />
            )}

            {showCreateModal && (
                <CreateTaskModal
                orgId={orgId || myOrgId}
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onTaskCreated={() => setSelectedStatus({ ...selectedStatus })}
                organizations={organizations}
              />
            )}


            {taskModalShow && (
                <TaskDetailsModal
                    task={viewTaskData}
                    show={taskModalShow}
                    onHide={() => {
                        setTaskModalShow(false);
                        setViewTaskData(null);
                    }}
                    onStatusSaved={(id, newStatus, newHistory) => {
                        setTasks(prev =>
                            prev.map(t =>
                                t.id === id
                                    ? { ...t, status: newStatus, statusHistory: newHistory }
                                    : t
                            )
                        );

                        setViewTaskData(prev =>
                            prev && prev.id === id
                                ? { ...prev, status: newStatus, statusHistory: newHistory }
                                : prev
                        );
                    }}
                    onNoteAdded={handleNoteAdded}
                    organizations={organizations}
                />
            )}
        </>
    );
}

export default TaskList;
